import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import { ApiResponse, AzureUploadUrl, IdNamePair, PaginatedResponse } from "@/types/global";
import {
  BookPaidSessionsInputs,
  DemoSessionDisplay,
  DemoSlotInfo,
  DemoSlotStatus,
  PaymentHistoryDisplay,
  StudentCourse,
  StudentCourseCertificate,
  StudentCourseCertificateInputs,
  StudentCourseDetailsDisplay,
  StudentCourseProgressDisplay,
  StudentSessionReportDisplay,
  TutoringBookDemoSessionInputs,
  TutoringModuleLessonPlan,
  TutoringModuleStudyNotes,
  TutoringSessionLogDisplay,
  TutoringStudentCoursePaymentsInputs,
  TutoringStudentDetailedDisplay,
  TutoringStudentDisplay,
  TutoringStudentInfo,
  TutoringStudentInputs,
  TutoringTutorDisplay,
  TutoringTutorInputs,
  TutoringTutorUpdate,
  TutorMonthlySalaryDisplay,
  TutorSessionDisplay,
  TutorSessionLog,
  TutorSessionLogInputs,
  TutorSessionReportDisplay,
  UpdateStudentDataInputs,
  UpdateTutorModuleInputs,
} from "@/types/tutoring";

export default class TutoringApi extends ApiVerified {
  async getStudentInfoForUserId(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TutoringStudentInfo>(
        `tutoring/get-student-info-for-user-id`,
        { params: { userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutoringCurrencies(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(`tutoring/get-currencies`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutoringCountries(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(`tutoring/get-countries`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutoringTimeZonesForCountry(countryId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `tutoring/get-time-zones-for-country`,
        { params: { countryId: countryId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutoringCourses(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `tutoring/get-tutoring-courses`,
        { params: { onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActiveTutoringUsersByRoleId(roleId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `tutoring/get-active-tutoring-users-by-role-id`,
        { params: { roleId: roleId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCoursesForStudent(studentId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentCourse>>(
        `tutoring/get-courses-for-student`,
        { params: { studentId: studentId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutorForStudentCourse(studentId: number, courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `tutoring/get-tutor-for-student-course`,
        { params: { studentId: studentId, courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async onboardNewStudent(newStudentInputs: TutoringStudentInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/onboard-new-student`,
        newStudentInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async bookDemoSession(bookDemoSession: TutoringBookDemoSessionInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/book-demo-session`,
        bookDemoSession,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedPaidStudents(
    minDate: string,
    maxDate: string,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TutoringStudentDisplay>>(
        `tutoring/get-detailed-paid-students`,
        { params: { minDate: minDate, maxDate: maxDate, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedDemoStudents(
    minDate: string,
    maxDate: string,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TutoringStudentDisplay>>(
        `tutoring/get-detailed-demo-students`,
        { params: { minDate: minDate, maxDate: maxDate, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutoringPaidStudents(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `tutoring/get-paid-students`,
        { params: { onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutoringDemoStudents(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `tutoring/get-demo-students`,
        { params: { onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getLessonPlansForTutorCourse(courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TutoringModuleLessonPlan>>(
        `tutoring/get-lesson-plans-for-course`,
        { params: { courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudyNotesForStudent(studentId: number, courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TutoringModuleStudyNotes>>(
        `tutoring/get-study-notes-for-student`,
        { params: { studentId: studentId, courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentCourseCertificates(studentId: number, courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentCourseCertificate>>(
        `tutoring/get-student-course-certificates`,
        { params: { studentId: studentId, courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentCourseSessionReports(studentId: number, courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentSessionReportDisplay>>(
        `tutoring/get-student-course-session-reports`,
        { params: { studentId: studentId, courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentSessionReports(studentId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentSessionReportDisplay>>(
        `tutoring/get-student-session-reports`,
        { params: { studentId: studentId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async bookPaidSessions(bookPaidSessionsInputs: BookPaidSessionsInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/book-paid-sessions`,
        bookPaidSessionsInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getUnassignedDemosForTutor(tutorId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<DemoSessionDisplay>>(
        `tutoring/get-unassigned-demo-sessions-for-tutor`,
        { params: { tutorId: tutorId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async acceptUnassignedDemo(demoSessionId: number, tutorId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`tutoring/accept-unassigned-demo`, {
        TutorId: tutorId,
        DemoSessionId: demoSessionId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutorSessionsByCategory(
    userId: number,
    categoryId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<TutorSessionDisplay>>(
        `tutoring/get-tutor-sessions-by-category`,
        {
          params: {
            userId: userId,
            categoryId: categoryId,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentSessionsByCategory(
    studentId: number,
    categoryId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<TutorSessionDisplay>>(
        `tutoring/get-student-sessions-by-category`,
        {
          params: {
            studentId: studentId,
            categoryId: categoryId,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDemoSessionsForAdmin(
    demoSessionStatusId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<DemoSessionDisplay>>(
        `tutoring/get-demo-sessions-for-admin`,
        {
          params: {
            demoSessionStatusId: demoSessionStatusId,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPaidSessionsForAdmin(
    categoryId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<TutorSessionDisplay>>(
        `tutoring/get-paid-sessions-for-admin`,
        {
          params: { categoryId: categoryId, pageNumber: pageNumber, pageSize: pageSize },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllDemoSlots(endDatetime: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<DemoSlotInfo>>(
        `tutoring/get-all-demo-slots`,
        { params: { endDatetime: endDatetime } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDemoSlotsForTutor(tutorId: number, endDatetime: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<DemoSlotStatus>>(
        `tutoring/get-demo-slots-for-tutor`,
        { params: { tutorId: tutorId, endDatetime: endDatetime } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentCertificateUploadUrl(
    studentId: number,
    courseId: number,
    certificateName: string,
    contentType: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrl>(
        `tutoring/get-student-certificate-upload-url`,
        {
          params: {
            studentId: studentId,
            courseId: courseId,
            certificateName: certificateName,
            contentType: contentType,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveStudentCourseCertificate(
    studentCourseCertificateInputs: StudentCourseCertificateInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/save-student-course-certificate`,
        {
          StudentId: studentCourseCertificateInputs.studentId,
          CourseId: studentCourseCertificateInputs.courseId,
          CertificateName: studentCourseCertificateInputs.certificateName,
          CertificateUrl: studentCourseCertificateInputs.certificateUrl,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutorMonthlySalaryReport(tutorId: number, reportMonth: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TutorMonthlySalaryDisplay>>(
        `tutoring/get-tutor-monthly-salary-report`,
        { params: { tutorId: tutorId, reportMonth: reportMonth } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateDemoSlotsForTutor(
    tutorId: number,
    slotStatuses: Array<DemoSlotStatus>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/update-demo-slots-for-tutor`,
        { TutorId: tutorId, SlotStatuses: slotStatuses },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async startPaidSession(tutorSessionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(`tutoring/start-paid-session`, {
        TutorSessionId: tutorSessionId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async endPaidSession(tutorSessionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(`tutoring/end-paid-session`, {
        TutorSessionId: tutorSessionId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTutorModule(updateTutorModuleInputs: UpdateTutorModuleInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`tutoring/update-tutor-module`, {
        ModuleId: updateTutorModuleInputs.ModuleId,
        LessonPlanUrl: updateTutorModuleInputs.LessonPlanUrl,
        PresentationUrl: updateTutorModuleInputs.PresentationUrl,
        StudyNotesUrl: updateTutorModuleInputs.StudyNotesUrl,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async studentCancelPaidSession(tutorSessionId: number) {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/student-cancel-paid-session`,
        { TutorSessionId: tutorSessionId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async tutorCancelPaidSession(
    tutorSessionId: number,
    cancellationReasonId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/tutor-cancel-paid-session`,
        { TutorSessionId: tutorSessionId, CancellationReasonId: cancellationReasonId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutorSessionReports(
    pageNumber: number,
    pageSize: number,
    tutorId: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<TutorSessionReportDisplay>>(
        `tutoring/get-tutor-session-reports`,
        { params: { pageNumber: pageNumber, pageSize: pageSize, tutorId: tutorId } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTutorSessionReport(
    tutorSessionLogId: number,
    sessionReportLink: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `tutoring/update-tutor-session-report`,
        { TutorSessionLogId: tutorSessionLogId, SessionReportLink: sessionReportLink },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async approveTutorSessionReport(tutorSessionLogId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `tutoring/approve-tutor-session-report`,
        { TutorSessionLogId: tutorSessionLogId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteSessionReport(tutorSessionLogId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `tutoring/delete-tutor-session-report`,
        { params: { tutorSessionLogId: tutorSessionLogId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async cancelDemoSession(demoSessionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`tutoring/cancel-demo-session`, {
        DemoSessionId: demoSessionId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCourseProgressForStudent(studentId: number, courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<StudentCourseProgressDisplay>(
        `tutoring/get-course-progress-for-student`,
        { params: { studentId: studentId, courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCoursePaymentHistoryForStudent(
    studentId: number,
    courseId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PaymentHistoryDisplay>>(
        `tutoring/get-course-payment-history-for-student`,
        { params: { studentId: studentId, courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateStudentCoursePayments(
    updateTutoringStudentCoursePaymentsInputs: TutoringStudentCoursePaymentsInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/update-student-course-payments`,
        updateTutoringStudentCoursePaymentsInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveTutorSessionLog(tutorSessionLogInputs: TutorSessionLogInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/save-tutor-session-log`,
        tutorSessionLogInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCoursesProgressAndPaymentsForStudent(studentId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentCourseDetailsDisplay>>(
        `tutoring/get-courses-progress-and-payments-for-student`,
        { params: { studentId: studentId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTutorSessionLog(updateTutorSessionLog: TutorSessionLog): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/update-tutor-session-log`,
        updateTutorSessionLog,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutorSessionLog(tutorSessionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TutoringSessionLogDisplay>(
        `tutoring/get-tutor-session-log`,
        { params: { tutorSessionId: tutorSessionId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentByEmail(emailId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TutoringStudentDetailedDisplay>(
        `tutoring/get-student-by-email`,
        { params: { emailId: emailId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateStudentDetails(updateStudentData: UpdateStudentDataInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/update-student-details`,
        updateStudentData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllDetailedTutors(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TutoringTutorDisplay>>(
        `tutoring/get-all-detailed-tutors`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addNewTutor(tutoringTutorInputs: TutoringTutorInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `tutoring/add-new-tutor`,
        tutoringTutorInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTutor(tutoringTutorUpdate: TutoringTutorUpdate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `tutoring/update-tutor`,
        tutoringTutorUpdate,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTutorsForCourse(courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `tutoring/get-tutors-for-course`,
        { params: { courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
